import { t } from '../i18n.js';

  class ReleaseNotes extends HTMLElement {

    // ── A. Shadow DOM setup ──────────────────────────────────────────────
    constructor() {
      super();
      this._shadow     = this.attachShadow({ mode: "open" });
      this._mode       = "onboarding"; // "onboarding" | "settings"
      this._version    = null;
      this._onDone     = null;
      this._pages      = [];           // extracted from fetched HTML
      this._textNext   = null;         // custom override for "Next" button (null = use i18n)
      this._textLast   = null;         // custom override for "Get started" button (null = use i18n)
      this._currentIdx = 0;
      this._lastReached = false;
    }

    // ── B. Lifecycle ─────────────────────────────────────────────────────
    connectedCallback() {
      this._renderShell();
      this._bindStaticEvents();
    }

    // ── C. Public API ────────────────────────────────────────────────────

    // mode: "onboarding" | "settings"
    set mode(v)    { this._mode = v; }

    // version string e.g. "1.2.3"
    set version(v) { this._version = v; }

    set next(v)    { this._textNext = v; }
    set last(v)    { this._textLast = v; }

    // callback fired when onboarding completes
    set onDone(fn) { this._onDone = typeof fn === "function" ? fn : null; }

    // Open the panel and fetch content
    open() {
      this._currentIdx  = 0;
      this._lastReached = false;
      this._renderShell(); // rebuild to apply mode
      this._bindStaticEvents();
      this._fetchAndRender();

      const overlay = this._shadow.getElementById("overlay");
      // Small delay so CSS transition fires
      requestAnimationFrame(() => {
        requestAnimationFrame(() => overlay.classList.add("visible"));
      });
    }

    // Close programmatically
    close() {
      const overlay = this._shadow.getElementById("overlay");
      overlay.classList.remove("visible");
    }

    // ── D. Fetch release HTML, parse into pages ───────────────────────────
    _fetchAndRender() {
      const url = `/releases/ver-${this._version}.html`;
      this._setLoading(true);

      fetch(url)
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.text();
        })
        .then(html => {
          this._pages = this._parsePages(html);
          this._setLoading(false);
          if (this._mode === "onboarding") {
            this._renderHorizontal();
          } else {
            this._renderVertical();
          }
        })
        .catch((err) => {
          
          console.log("⚠️ Release not not found !! (%s)", url);
          console.log("error %o", err)
          this._pages = this._demoPages(this._version);
          this._setLoading(false);
          if (this._mode === "onboarding") {
            this._renderHorizontal();
          } else {
            this._renderVertical();
          }
          //
          //this._setLoading(false);
          //this.close();          
        });
    }

    // ── E. Parse fetched HTML into page objects ───────────────────────────
    // Expected format: sections with class "release-page" inside the fetched HTML.
    // Falls back to treating the entire HTML as one page.
    _parsePages(html) {
      const parser = new DOMParser();
      const doc    = parser.parseFromString(html, "text/html");
      const nodes  = doc.querySelectorAll(".release-page");

      if (nodes.length > 0) {
        return Array.from(nodes).map(n => ({
          icon:    n.dataset.icon    || "✦",
          title:   n.dataset.title   || "",
          color:   n.dataset.color   || "#1a1a1a",
          content: n.innerHTML,
        }));
      }

      // Single-page fallback
      return [{ icon: "✦", title: t("rn-whatsnew-in", { ver: this._version }), color: "#1a1a1a", content: doc.body.innerHTML }];
    }

    // ── F. Demo pages (used when fetch fails / for development) ──────────
    _demoPages(version) {
      return [
        {
          icon: "☕",
          title: "App CC-Caffè",
          color: "#854F0B",
          content: `Benvenuto nella nuova versione !!`,
        },
      ];
    }

    // ── G. Loading state ─────────────────────────────────────────────────
    _setLoading(on) {
      const loader  = this._shadow.getElementById("loader");
      const content = this._shadow.getElementById("content-area");
      if (loader)  loader.style.display  = on ? "flex" : "none";
      if (content) content.style.display = on ? "none"  : "block";
    }

    // ── H. Render horizontal / onboarding mode ────────────────────────────
    _renderHorizontal() {
      const contentArea = this._shadow.getElementById("content-area");
      if (!contentArea) return;

      contentArea.innerHTML = `
        <div class="horiz-track" id="horiz-track">
          ${this._pages.map((p, i) => `
            <div class="horiz-slide" data-idx="${i}">
              <div class="slide-headline">  <span class="slide-icon" style="color:${p.color}">${p.icon}</span> <span class="slide-title">${p.title}</span></div>
              
              <div class="slide-body">${p.content}</div>
            </div>
          `).join("")}
        </div>
        <div class="dots" id="dots">
          ${this._pages.map((_, i) => `<button class="dot${i===0?" active":""}" data-dot="${i}" aria-label="${t("rn-go-to-page", { n: i+1 })}"></button>`).join("")}
        </div>
        <div class="horiz-footer" id="horiz-footer">
          <button class="btn-next" id="btn-next">${this._textNext || t("rn-btn-next")}</button>
        </div>
      `;

      this._syncHorizState();
      this._bindHorizEvents();

      // Override title/close visibility per slide
      this._syncHeaderForMode();
    }

    // ── I. Render vertical / settings mode ───────────────────────────────
    _renderVertical() {
      const contentArea = this._shadow.getElementById("content-area");
      if (!contentArea) return;

      contentArea.innerHTML = `
        <div class="vert-pages">
          ${this._pages.map((p, i) => `
            <div class="vert-page${i < this._pages.length - 1 ? " vert-page-sep" : ""}">
              <div class="vert-page-header">
                <span class="vert-icon" style="color:${p.color}">${p.icon}</span>
                <h2 class="vert-title">${p.title}</h2>
              </div>
              <div class="vert-body">${p.content}</div>
            </div>
          `).join("")}
          <p class="vert-footer-note">${t("rn-version")} ${this._version} · Released ${new Date().toLocaleDateString("en-CH", { day:"numeric", month:"long", year:"numeric" })}</p>
        </div>
      `;
    }

    // ── J. Sync horizontal UI state (dots, button label, close visibility) ─
    _syncHorizState() {
      const track     = this._shadow.getElementById("horiz-track");
      const dots      = this._shadow.querySelectorAll(".dot");
      const btnNext   = this._shadow.getElementById("btn-next");
      const btnClose  = this._shadow.getElementById("btn-close");
      const i         = this._currentIdx;
      const isLast    = i === this._pages.length - 1;

      // Scroll track
      if (track) {
        const slideW = track.offsetWidth;
        track.scrollTo({ left: slideW * i, behavior: "smooth" });
      }

      // Dots
      dots.forEach((d, di) => d.classList.toggle("active", di === i));

      // Button
      if (btnNext) btnNext.textContent = isLast ? (this._textLast || t("rn-btn-get-started")) : (this._textNext || t("rn-btn-next"));

      // Close button: only show once last page is reached
      if (isLast) this._lastReached = true;
      //if (btnClose) btnClose.style.display = this._lastReached ? "flex" : "none";
    }

    _syncHeaderForMode() {
      const btnClose  = this._shadow.getElementById("btn-close");
      //if (this._mode === "onboarding" && btnClose) {
      //  btnClose.style.display = this._lastReached ? "flex" : "none";
      //}
    }

    // ── K. Bind horizontal events ─────────────────────────────────────────
    _bindHorizEvents() {
      const track    = this._shadow.getElementById("horiz-track");
      const btnNext  = this._shadow.getElementById("btn-next");
      const dots     = this._shadow.querySelectorAll("[data-dot]");

      // Next / Get started
      btnNext?.addEventListener("click", () => {
        if (this._currentIdx < this._pages.length - 1) {
          this._currentIdx++;
          this._syncHorizState();
        } else {
          this.close();
          if (this._onDone) this._onDone();
        }
      });

      // Dot navigation
      dots.forEach(d => {
        d.addEventListener("click", () => {
          const target = Number(d.dataset.dot);
          // Only allow going back or to already-unlocked slides
          if (target <= this._currentIdx || this._lastReached) {
            this._currentIdx = target;
            this._syncHorizState();
          }
        });
      });

      // Touch swipe
      if (track) {
        let startX = 0;
        track.addEventListener("touchstart", e => {
          startX = e.touches[0].clientX;
        }, { passive: true });

        track.addEventListener("touchend", e => {
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) < 40) return;
          if (dx < 0 && this._currentIdx < this._pages.length - 1) {
            this._currentIdx++;
          } else if (dx > 0 && this._currentIdx > 0) {
            this._currentIdx--;
          }
          this._syncHorizState();
        }, { passive: true });

        // Update index when scroll snaps (mouse/trackpad)
        let scrollTimer;
        track.addEventListener("scroll", () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const slideW = track.offsetWidth;
            const newIdx = Math.round(track.scrollLeft / slideW);
            if (newIdx !== this._currentIdx) {
              // Only allow advancing if it's forward or already reached end
              if (newIdx <= this._currentIdx || this._lastReached || newIdx < this._currentIdx) {
                this._currentIdx = newIdx;
              } else {
                // Snap back — user tried to swipe past locked page
                track.scrollTo({ left: slideW * this._currentIdx, behavior: "smooth" });
                return;
              }
              this._syncHorizState();
            }
          }, 80);
        });
      }
    }

    // ── L. Full shell render ──────────────────────────────────────────────
    _renderShell() {
      const isOnboarding = this._mode === "onboarding";

      this._shadow.innerHTML = `
        <style>
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

          :host { 
            display: block; 
          }

          /* ── Overlay ── */
          .overlay {
            position: fixed;
            inset: 0;
            z-index: 1100;
            background: rgba(0,0,0,0);
            display: flex;
            align-items: flex-end;
            justify-content: center;
            transition: background 0.3s;
            pointer-events: none;
          }

          .overlay.visible {
            background: rgba(0,0,0,0.45);
            pointer-events: all;
          }

          /* ── Sheet ── */
          .sheet {
            width: 100%;
            max-width: 90dvw;
            background: #fff;
            border-radius: 20px 20px 0 0;
            border: 0.5px solid #e0dfd7;
            border-bottom: none;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            max-height: 92dvh;

            transform: translateY(100%);
            transition: transform 0.36s cubic-bezier(0.32, 0.72, 0, 1);
          }

          .overlay.visible .sheet {
            transform: translateY(0);
          }

          /* ── Sheet header ── */
          .sheet-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 20px 12px;
            border-bottom: 0.5px solid #e0dfd7;
            flex-shrink: 0;
          }

          .sheet-handle {
            position: absolute;
            top: 8px;
            left: 50%;
            transform: translateX(-50%);
            width: 36px; height: 4px;
            border-radius: 2px;
            background: #e0dfd7;
            flex-shrink: 0;
          }

          .header-left {
            display: flex;
            flex-direction: column;
            gap: 1px;
          }

          .header-label {
            font-size: 11px;
            font-weight: 500;
            color: #aaa;
            letter-spacing: 0.05em;
            text-transform: uppercase;
          }

          .header-version {
            font-size: 16px;
            font-weight: 500;
            color: #1a1a1a;
          }

          .btn-close {
            width: 32px; height: 32px;
            border-radius: 8px;
            border: 0.5px solid #e0dfd7;
            background: #5D4037;
            color: #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: background 0.12s, color 0.12s;
            flex-shrink: 0;
          }

          .btn-close:hover { background: #eee; color: #1a1a1a; }

          /* ── Loader ── */
          #loader {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 48px 20px;
            font-size: 13px;
            color: #aaa;
          }

          .spinner {
            width: 16px; height: 16px;
            border: 1.5px solid #e0dfd7;
            border-top-color: #888;
            border-radius: 50%;
            animation: spin 0.7s linear infinite;
          }

          @keyframes spin { to { transform: rotate(360deg); } }

          /* ── Content area ── */
          #content-area { display: none; flex: 1; overflow: hidden; }

          /* ══════════════════════════════
             HORIZONTAL (onboarding) mode
          ══════════════════════════════ */
          .horiz-track {
            display: flex;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            height: 100%;
          }
          .horiz-track::-webkit-scrollbar { display: none; }

          .horiz-slide {
            flex: 0 0 100%;
            scroll-snap-align: start;
            padding: 28px 28px 16px;
            display: flex;
            flex-direction: column;
            gap: 8px;
            overflow-y: auto;
            scrollbar-width: none;
          }
          .horiz-slide::-webkit-scrollbar { display: none; }

          .slide-headline {
            display: flex;
            align-items: center; /* vertically centers all children */
            gap: 0.5rem;         /* optional spacing between icon and title */
          }

          
          .slide-icon {
            font-size: 44px;
            line-height: 1;
            margin-bottom: 6px;
          }

          .slide-title {
            font-size: 20px;
            font-weight: 500;
            color: #1a1a1a;
            line-height: 1;
            margin-bottom: 4px;
          }

          .slide-body {
            font-size: 14px;
            color: #555;
            line-height: 1.65;
          }

          .slide-body p { margin-bottom: 10px; }
          .slide-body ul { padding-left: 1.2em; display: flex; flex-direction: column; gap: 6px; }
          .slide-body li { color: #555; }
          .slide-body strong { color: #1a1a1a; font-weight: 500; }

          /* Dots */
          .dots {
            display: flex;
            justify-content: center;
            gap: 7px;
            padding: 12px 0 8px;
            flex-shrink: 0;
          }

          .dot {
            width: 7px; height: 7px;
            border-radius: 50%;
            border: none;
            background: #d0cfc7;
            cursor: pointer;
            transition: background 0.2s, transform 0.2s;
            padding: 0;
          }

          .dot.active {
            background: #1a1a1a;
            transform: scale(1.25);
          }

          /* Footer */
          .horiz-footer {
            padding: 10px 20px 20px;
            flex-shrink: 0;
          }

          .btn-next {
            width: 100%;
            padding: 12px;
            font: inherit;
            font-size: 15px;
            font-weight: 500;
            border-radius: 8px;
            border: none;
            background: #5D4037;
            color: #fff;
            cursor: pointer;
            transition: opacity 0.15s, transform 0.1s;
          }

          .btn-next:hover  { opacity: 0.85; }
          .btn-next:active { transform: scale(0.98); }

          /* ══════════════════════════════
             VERTICAL (settings) mode
          ══════════════════════════════ */
          #content-area.vert-mode {
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }

          .vert-pages {
            padding: 12px 20px 32px;
            display: flex;
            flex-direction: column;
            gap: 0;
          }

          .vert-page {
            padding: 20px 0;
          }

          .vert-page-sep {
            border-bottom: 0.5px solid #e0dfd7;
          }

          .vert-page-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 12px;
          }

          .vert-icon {
            font-size: 28px;
            line-height: 1;
            flex-shrink: 0;
          }

          .vert-title {
            font-size: 16px;
            font-weight: 500;
            color: #1a1a1a;
          }

          .vert-body {
            font-size: 14px;
            color: #555;
            line-height: 1.65;
          }

          .vert-body p  { margin-bottom: 10px; }
          .vert-body ul { padding-left: 1.2em; display: flex; flex-direction: column; gap: 6px; }
          .vert-body li { color: #555; }
          .vert-body strong { color: #1a1a1a; font-weight: 500; }

          .vert-footer-note {
            font-size: 12px;
            color: #aaa;
            padding-top: 16px;
          }

          /* ── Horizontal content wrapper height ── */
          .horiz-content-wrap {
            flex: 1;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            min-height: 0;
          }

          li.bug {
              margin-left: 15px;

              .id {
                font-weight: 800;
                font-size: 16px;
              }
          }
          

          /* ── Dark mode ── */
          /*
          @media (prefers-color-scheme: dark) {
            .sheet         { background: #1e1e1c; border-color: #333330; }
            .sheet-header  { border-bottom-color: #333330; }
            .sheet-handle  { background: #333330; }
            .header-version { color: #f0ede8; }
            .btn-close     { background: #2a2a28; border-color: #333330; }
            .btn-close:hover { background: #333330; color: #f0ede8; }
            .slide-title, .vert-title { color: #f0ede8; }
            .slide-body, .vert-body   { color: #888; }
            .slide-body li, .vert-body li { color: #888; }
            .slide-body strong, .vert-body strong { color: #f0ede8; }
            .dot           { background: #444441; }
            .dot.active    { background: #f0ede8; }
            .btn-next      { background: #f0ede8; color: #1a1a1a; }
            .vert-page-sep { border-bottom-color: #333330; }
            .vert-footer-note { color: #555; }
          }
          */

          /* ── Safe area ── */
          .horiz-footer {
            padding-bottom: max(20px, env(safe-area-inset-bottom, 20px));
          }
        </style>

        <div class="overlay" id="overlay">
          <div class="sheet" role="dialog" aria-modal="true"
               aria-label="${t("rn-whatsnew-in", { ver: this._version || "" })}">
            <div style="position:relative">
              <div class="sheet-handle"></div>
            </div>

            <!-- Header -->
            <div class="sheet-header">
              <div class="header-left">
                <span class="header-label">${t("rn-header-whatsnew")}</span>
                <span class="header-version">${t("rn-version")} ${this._version || ""}</span>
              </div>
              <button class="btn-close" id="btn-close"
                      aria-label="${t("rn-close")}"
                      __style="display:${isOnboarding ? "none" : "flex"}"
                      style="display: none;">
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                  <path d="M2 2l10 10M12 2L2 12"
                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>

            <!-- Loader -->
            <div id="loader" style="display:flex">
              <div class="spinner"></div>
              ${t("rn-loading")}
            </div>

            <!-- Content (populated after fetch) -->
            <div id="content-area" class="${isOnboarding ? "" : "vert-mode"}">
            </div>

          </div>
        </div>
      `;
    }

    // ── M. Bind static events (close button, overlay backdrop) ───────────
    _bindStaticEvents() {
      const overlay = this._shadow.getElementById("overlay");
      const btnClose = this._shadow.getElementById("btn-close");

      btnClose?.addEventListener("click", () => this.close());

      // Backdrop tap → close only in settings mode
      overlay?.addEventListener("click", e => {
        if (e.target === overlay && this._mode === "settings") {
          this.close();
        }
      });
    }
  }

 export { ReleaseNotes }